import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {
  Stack,
  Card,
  Avatar,
  TextStyle,
  Icon,
  Heading,
  Button,
  Popover,
  ActionList,
  Modal,
} from '@shopify/polaris';
import {
  MobileHorizontalDotsMajorMonotone,
  LocationMajorMonotone,
} from '@shopify/polaris-icons';
import {removeOrganization} from '../../actions/context';
import {ProfileImage} from 'sections/services-marketplace/experts/components/profile-image';

class OrganizationCard extends React.Component {
  state = {
    popoverActive: false,
    modalOpen: false,
  };

  togglePopover = () => {
    this.setState(({popoverActive}) => ({popoverActive: !popoverActive}));
  };

  toggleModal = () => {
    this.setState(({modalOpen}) => ({modalOpen: !modalOpen}));
  };

  onRemoveOrganization = () => {
    const {onRemoveOrganization} = this.props;

    this.toggleModal();
    onRemoveOrganization();
  };

  renderHeader() {
    const {popoverActive} = this.state;
    const {organization} = this.props;

    const PopoverActivator = (
      <div className="OrganizationCard__PopoverActivator">
        <Button onClick={this.togglePopover} plain>
          <Icon source={MobileHorizontalDotsMajorMonotone} color="ink" />
        </Button>
      </div>
    );

    const PopoverActions = [
      {
        content: 'Visit Profile',
        external: true,
        url: organization.public_url,
      },
      {
        content: 'Remove Expert',
        destructive: true,
        onAction: this.toggleModal,
      },
    ];

    return (
      <Stack wrap={false}>
        <Stack.Item fill>
          <Heading>Your job will be sent to the following expert</Heading>
        </Stack.Item>
        <Popover
          active={popoverActive}
          activator={PopoverActivator}
          onClose={this.togglePopover}
          preferredAlignment="right"
        >
          <div className="OrganizationCard__PopoverActions">
            <ActionList items={PopoverActions} />
          </div>
        </Popover>
        <Stack.Item />
      </Stack>
    );
  }

  renderContent() {
    const {organization} = this.props;

    return (
      <Stack alignment="center">
        <Stack.Item>{CardAvatar(organization)}</Stack.Item>
        <Stack.Item>{CardContent(organization)}</Stack.Item>
      </Stack>
    );
  }

  renderModal() {
    const {modalOpen} = this.state;
    const {organization} = this.props;

    return (
      <Modal
        open={modalOpen}
        onClose={this.toggleModal}
        title="Remove expert from this job?"
        primaryAction={{
          content: 'Remove expert',
          onAction: this.onRemoveOrganization,
          destructive: true,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: this.toggleModal,
          },
        ]}
      >
        <Modal.Section>
          <p>
            If you remove {organization.businessName} from this job, your job
            will be sent to other recommended experts that do this type of work.
          </p>
        </Modal.Section>
      </Modal>
    );
  }

  render() {
    return (
      <div className="OrganizationCard">
        <Card sectioned>
          <Stack vertical>
            {this.renderHeader()}
            {this.renderContent()}
          </Stack>
        </Card>
        {this.renderModal()}
      </div>
    );
  }
}

function CardAvatar(organization) {
  if (isGravatar(organization.imageUrl)) {
    const initials = generateInitials(organization.businessName);
    return <Avatar initials={initials} size="large" />;
  }

  return <ProfileImage source={organization.imageUrl} size="Small" />;
}

function CardContent(organization) {
  return (
    <div className="OrganizationCard__Content">
      <Stack spacing="extraTight" vertical>
        <Heading>{organization.businessName}</Heading>
        <Stack spacing="extraTight" alignment="center">
          <Icon source={LocationMajorMonotone} color="inkLightest" />
          <TextStyle variation="subdued">{organization.location}</TextStyle>
        </Stack>
      </Stack>
    </div>
  );
}

function isGravatar(imageUrl) {
  return imageUrl.includes('https://www.gravatar.com');
}

function generateInitials(businessName) {
  return businessName.substring(0, 2).toUpperCase();
}

function mapStateToProps(store) {
  return {
    organization: store.context.organization,
  };
}

const mapDispatchToProps = {
  onRemoveOrganization: removeOrganization,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OrganizationCard);

OrganizationCard.propTypes = {
  onRemoveOrganization: PropTypes.func.isRequired,
  organization: PropTypes.shape({
    imageUrl: PropTypes.string.isRequired,
    businessName: PropTypes.string.isRequired,
    organizationId: PropTypes.number.isRequired,
    location: PropTypes.string.isRequired,
    public_url: PropTypes.string.isRequired, // eslint-disable-line camelcase
  }).isRequired,
};
